/**
 * useDrafts hook
 * Manages draft check-ins in AsyncStorage.
 *
 * Draft photos: on native, picked photos live in expo's picker/manipulator cache
 * dirs, which are purged after every submit or discard. Saving a draft therefore
 * copies its photos into a stable per-draft directory (see
 * `@/utils/image-temp-files`), and deleting the draft deletes that directory.
 * On web, photos are `blob:` URIs that die with the page, so they are never
 * persisted; the draft records how many were dropped so the restore path can
 * tell the user (`getDraftRestoreNotice`). Saving reports photos that could not
 * be copied or kept (`SaveDraftResult`) so the UI can warn at save time.
 *
 * Per-photo alt text and MIME type live in `photoDetails`, matched to `photos`
 * by URI. `photos` stays a URI list so older drafts (URIs only) still read.
 */

import AsyncStorage from '@react-native-async-storage/async-storage';
import { useState, useCallback, useEffect } from 'react';
import { Platform } from 'react-native';
import {
  deleteAllDraftPhotos,
  deleteDraftPhotos,
  draftPhotoExists,
  persistDraftPhotos,
} from '@/utils/image-temp-files';

/**
 * Generate a simple unique ID for drafts
 */
function generateDraftId(): string {
  return `draft_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
}

/** A draft photo with the details needed to upload it after a restore. */
export interface DraftPhoto {
  uri: string;
  altText?: string;
  mimeType?: string;
  /** Already uploaded to the check-in in `resume` (a retry skips it). */
  uploaded?: boolean;
}

/**
 * A check-in that was already created by a submit that did not fully succeed.
 * Restoring the draft resumes against it instead of posting a second check-in.
 */
export interface DraftCheckInResume {
  checkInId: string;
  reviewDone: boolean;
  finalized: boolean;
}

/**
 * Per-entry draft data for multi-entry check-ins
 */
export interface CheckInDraftEntry {
  menuItem: { id: string; name: string } | null;
  ratingId: number | null;
  photos: string[]; // Local URIs
  /** Alt text and MIME type per photo, matched to `photos` by URI. Absent in older drafts. */
  photoDetails?: DraftPhoto[];
  reviewContent: string;
  /** Set when this entry's check-in already exists on the server. */
  resume?: DraftCheckInResume;
}

/** What a save could not keep, so the UI can warn instead of a plain "Draft saved". */
export interface SaveDraftResult {
  /** Native: photos whose stable copy failed; they may not survive a cache purge. */
  failedPhotoCount: number;
  /** Web: photos (blob:/data: URIs) that cannot be stored at all. */
  droppedPhotoCount: number;
}

export interface SavedDraft extends SaveDraftResult {
  id: string;
}

function isDraftPhoto(value: unknown): value is DraftPhoto {
  return (
    typeof value === 'object' &&
    value !== null &&
    typeof (value as DraftPhoto).uri === 'string'
  );
}

/**
 * The photos of a draft entry with their details, in order. Reads older drafts
 * that stored URIs only; details that no longer match a URI are ignored.
 */
export function getDraftEntryPhotos(
  entry: Pick<CheckInDraftEntry, 'photos' | 'photoDetails'>
): DraftPhoto[] {
  const details = new Map(
    (Array.isArray(entry.photoDetails) ? entry.photoDetails : [])
      .filter(isDraftPhoto)
      .map((detail) => [detail.uri, detail])
  );
  return entry.photos.map((uri) => details.get(uri) ?? { uri });
}

/**
 * Check-in draft structure.
 * Legacy single-entry fields (menuItem, ratingId, photos, reviewContent) are kept
 * for backward compatibility. The `entries` array is the canonical source when present.
 */
export interface CheckInDraft {
  id: string; // Local UUID
  checkInId: string | null; // Backend ID if draft was synced
  business: { id: string; name: string } | null;
  /** @deprecated Use entries[0] instead. Kept for backward compat with old drafts. */
  menuItem: { id: string; name: string } | null;
  /** @deprecated Use entries[0] instead. */
  ratingId: number | null;
  diningType: 'dine_in' | 'takeout';
  /** @deprecated Use entries[0] instead. */
  photos: string[]; // Local URIs
  /** @deprecated Use entries[0] instead. */
  reviewContent: string;
  /** Multi-entry data. When present, takes precedence over legacy fields. */
  entries?: CheckInDraftEntry[];
  /** Photos that could not be kept (web blob: URIs, evicted native files). */
  droppedPhotoCount?: number;
  createdAt: string; // ISO timestamp
  updatedAt: string; // ISO timestamp
}

// Storage key
const STORAGE_KEY = '@flavrly/check-in-drafts';

/** URI schemes that only live as long as the web page (or bloat storage). */
const WEB_EPHEMERAL_URI_SCHEMES = ['blob:', 'data:'];

function isWeb(): boolean {
  return Platform.OS === 'web';
}

function isPersistableOnWeb(uri: string): boolean {
  return !WEB_EPHEMERAL_URI_SCHEMES.some((scheme) => uri.startsWith(scheme));
}

/** Every distinct photo URI in a draft, entries first, then the legacy field. */
function collectDraftPhotoUris(draft: CheckInDraft): string[] {
  const entryPhotos = (draft.entries ?? []).flatMap((entry) => entry.photos);
  return [...new Set([...entryPhotos, ...draft.photos])];
}

/** Maps a photo URI to its replacement, or to null to drop the photo. */
type PhotoUriTransform = (uri: string) => string | null;

function transformUris(uris: string[], transform: PhotoUriTransform): string[] {
  return uris.flatMap((uri) => {
    const next = transform(uri);
    return next === null ? [] : [next];
  });
}

function transformDetails(details: DraftPhoto[], transform: PhotoUriTransform): DraftPhoto[] {
  return details.filter(isDraftPhoto).flatMap((detail) => {
    const next = transform(detail.uri);
    return next === null ? [] : [{ ...detail, uri: next }];
  });
}

/** Return a copy of the draft with every photo URI (and its details) transformed. */
function mapDraftPhotos(draft: CheckInDraft, transform: PhotoUriTransform): CheckInDraft {
  return {
    ...draft,
    photos: transformUris(draft.photos, transform),
    ...(draft.entries
      ? {
          entries: draft.entries.map((entry) => ({
            ...entry,
            photos: transformUris(entry.photos, transform),
            ...(Array.isArray(entry.photoDetails)
              ? { photoDetails: transformDetails(entry.photoDetails, transform) }
              : {}),
          })),
        }
      : {}),
  };
}

/** Remove photos failing `keep`; count the distinct URIs removed. */
function dropDraftPhotos(
  draft: CheckInDraft,
  keep: (uri: string) => boolean
): { draft: CheckInDraft; dropped: number } {
  const dropped = collectDraftPhotoUris(draft).filter((uri) => !keep(uri)).length;
  if (dropped === 0) return { draft, dropped };
  return { draft: mapDraftPhotos(draft, (uri) => (keep(uri) ? uri : null)), dropped };
}

/**
 * Prepare a draft's photos for storage: stable copies on native, no ephemeral
 * URIs on web. Also reports what could not be kept.
 */
async function prepareDraftPhotosForStorage(
  draft: CheckInDraft
): Promise<{ draft: CheckInDraft; result: SaveDraftResult }> {
  if (isWeb()) {
    const { draft: kept, dropped } = dropDraftPhotos(draft, isPersistableOnWeb);
    return {
      draft: { ...kept, droppedPhotoCount: dropped },
      result: { failedPhotoCount: 0, droppedPhotoCount: dropped },
    };
  }

  const originals = collectDraftPhotoUris(draft);
  const { uris: persisted, failedCount } = await persistDraftPhotos(draft.id, originals);
  const byOriginal = new Map(originals.map((uri, index) => [uri, persisted[index] ?? uri]));
  return {
    draft: mapDraftPhotos(draft, (uri) => byOriginal.get(uri) ?? uri),
    result: { failedPhotoCount: failedCount, droppedPhotoCount: 0 },
  };
}

/** Drop photos that cannot be shown after a restore and count them. */
function sanitizeDraftForRestore(draft: CheckInDraft): CheckInDraft {
  const keep = isWeb() ? isPersistableOnWeb : draftPhotoExists;
  const { draft: kept, dropped } = dropDraftPhotos(draft, keep);
  if (dropped === 0) return draft;
  return { ...kept, droppedPhotoCount: (draft.droppedPhotoCount ?? 0) + dropped };
}

export interface DraftRestoreNotice {
  title: string;
  description: string;
}

/**
 * The notice to show when a restored draft lost photos, or null. Intended for
 * the draft restore path (`useDraftLoader`).
 */
export function getDraftRestoreNotice(draft: CheckInDraft): DraftRestoreNotice | null {
  const count = draft.droppedPhotoCount ?? 0;
  if (count <= 0) return null;
  if (count === 1) {
    return {
      title: 'Photo not restored',
      description: 'The photo in this draft could not be restored. Please add it again.',
    };
  }
  return {
    title: `${count} photos not restored`,
    description: 'The photos in this draft could not be restored. Please add them again.',
  };
}

/**
 * The warning to show instead of "Draft saved" when the save could not keep
 * every photo, or null.
 */
export function getDraftSaveNotice(result: SaveDraftResult): DraftRestoreNotice | null {
  if (result.failedPhotoCount > 0) {
    const n = result.failedPhotoCount;
    return {
      title: `Draft saved, but ${n === 1 ? '1 photo' : `${n} photos`} may be lost`,
      description:
        n === 1
          ? 'The photo could not be stored with the draft. Add it again if it is missing later.'
          : 'Some photos could not be stored with the draft. Add them again if they are missing later.',
    };
  }
  if (result.droppedPhotoCount > 0) {
    const n = result.droppedPhotoCount;
    return {
      title: n === 1 ? 'Draft saved without its photo' : `Draft saved without ${n} photos`,
      description: 'Photos cannot be kept in a draft on the web. Add them again when you continue.',
    };
  }
  return null;
}

/**
 * Get all drafts from AsyncStorage
 */
export async function getDraftsFromStorage(): Promise<CheckInDraft[]> {
  try {
    const draftsJson = await AsyncStorage.getItem(STORAGE_KEY);
    if (!draftsJson) {
      return [];
    }

    const drafts = JSON.parse(draftsJson) as CheckInDraft[];

    // Sort by updatedAt (most recent first)
    return drafts.sort((a, b) =>
      new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
    );
  } catch (error) {
    console.error('Failed to get drafts from storage:', error);
    return [];
  }
}

/**
 * Save a draft to AsyncStorage. Resolves with the photos that could not be kept.
 */
export async function saveDraftToStorage(input: CheckInDraft): Promise<SaveDraftResult> {
  try {
    const { draft, result } = await prepareDraftPhotosForStorage(input);
    const drafts = await getDraftsFromStorage();

    // Check if draft already exists
    const existingIndex = drafts.findIndex(d => d.id === draft.id);

    if (existingIndex >= 0) {
      // Update existing draft
      drafts[existingIndex] = {
        ...draft,
        updatedAt: new Date().toISOString(),
      };
    } else {
      // Add new draft
      drafts.push({
        ...draft,
        createdAt: draft.createdAt || new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      });
    }

    await AsyncStorage.setItem(STORAGE_KEY, JSON.stringify(drafts));
    return result;
  } catch (error) {
    console.error('Failed to save draft to storage:', error);
    throw error;
  }
}

/**
 * Delete a draft from AsyncStorage
 */
export async function deleteDraftFromStorage(draftId: string): Promise<void> {
  try {
    const drafts = await getDraftsFromStorage();
    const updatedDrafts = drafts.filter(d => d.id !== draftId);
    await AsyncStorage.setItem(STORAGE_KEY, JSON.stringify(updatedDrafts));
    await deleteDraftPhotos(draftId);
  } catch (error) {
    console.error('Failed to delete draft from storage:', error);
    throw error;
  }
}

/**
 * Get a single draft by ID, prepared for restore: photos that can no longer be
 * shown are dropped and counted in `droppedPhotoCount`.
 */
export async function getDraftByIdFromStorage(draftId: string): Promise<CheckInDraft | null> {
  try {
    const drafts = await getDraftsFromStorage();
    const draft = drafts.find(d => d.id === draftId);
    return draft ? sanitizeDraftForRestore(draft) : null;
  } catch (error) {
    console.error('Failed to get draft by ID from storage:', error);
    return null;
  }
}

/**
 * Clear all drafts and their stored photos (useful for testing)
 */
export async function clearAllDrafts(): Promise<void> {
  try {
    await AsyncStorage.removeItem(STORAGE_KEY);
    await deleteAllDraftPhotos();
  } catch (error) {
    console.error('Failed to clear all drafts:', error);
    throw error;
  }
}

/**
 * useDrafts hook
 * Provides stateful access to draft check-ins
 */
export function useDrafts() {
  const [drafts, setDrafts] = useState<CheckInDraft[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadDrafts = useCallback(async (): Promise<CheckInDraft[]> => {
    try {
      setIsLoading(true);
      setError(null);
      const loadedDrafts = await getDraftsFromStorage();
      setDrafts(loadedDrafts);
      return loadedDrafts;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load drafts';
      setError(errorMessage);
      console.error('Failed to load drafts:', err);
      return [];
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Load drafts on mount
  useEffect(() => {
    loadDrafts();
  }, [loadDrafts]);

  const saveDraft = useCallback(async (draft: Omit<CheckInDraft, 'id' | 'createdAt' | 'updatedAt'> & { id?: string }): Promise<SavedDraft> => {
    try {
      setError(null);

      // Generate ID if not provided
      const draftId = draft.id || generateDraftId();

      const draftToSave: CheckInDraft = {
        ...draft,
        id: draftId,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };

      const result = await saveDraftToStorage(draftToSave);

      // Reload drafts to update state
      await loadDrafts();

      return { id: draftId, ...result };
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to save draft';
      setError(errorMessage);
      throw err;
    }
  }, [loadDrafts]);

  const deleteDraft = useCallback(async (draftId: string) => {
    try {
      setError(null);
      await deleteDraftFromStorage(draftId);

      // Reload drafts to update state
      await loadDrafts();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to delete draft';
      setError(errorMessage);
      throw err;
    }
  }, [loadDrafts]);

  const getDraftById = useCallback((draftId: string): CheckInDraft | null => {
    return drafts.find(d => d.id === draftId) || null;
  }, [drafts]);

  const refreshDrafts = useCallback((): Promise<CheckInDraft[]> => {
    return loadDrafts();
  }, [loadDrafts]);

  return {
    drafts,
    isLoading,
    error,
    saveDraft,
    deleteDraft,
    getDraftById,
    refreshDrafts,
  };
}
